import { Injectable, computed, inject } from '@angular/core';
import { HttpErrorResponse, httpResource } from '@angular/common/http';
import { Observable } from 'rxjs';
import { LicencesService } from '../Services/licences.service';
import { UsersService } from '../Services/users.service';
import { ASSIGNABLE_USERS_PAGE_SIZE } from '../Consts/licences.const';
import { IndexResponse } from '../Interfaces/api/users.interface';
import { ApiErrorResponse } from '../Modules/Shared/Interfaces/response.interface';
import {
    Licence,
    LicenceComponentSummary,
    LicenceMutationResponse,
    LicenceOverview,
    LicenceOverviewResponse,
} from '../Interfaces/api/licences.interface';
import { User } from '../Models/avyna/user.model';
import { LicenceComponentType } from '../Types/licence-component-type.type';
import { ResourceStateService } from '../Modules/Shared/Services/resource-state.service';

@Injectable()
export class LicencesFacade {
    private readonly _resourceState = inject(ResourceStateService);

    private readonly _licencesService = inject(LicencesService);
    private readonly _usersService = inject(UsersService);

    private readonly _overviewResource = httpResource<LicenceOverviewResponse>(() =>
        this._licencesService.index(),
    );

    private readonly _usersResource = httpResource<IndexResponse>(() =>
        this._usersService.index({ page: 0, pageSize: ASSIGNABLE_USERS_PAGE_SIZE }),
    );

    // ACTIONS
    public reload() {
        this._overviewResource.reload();
        this._usersResource.reload();
    }

    public buy(quantity: number, componentType: LicenceComponentType): Observable<LicenceOverviewResponse> {
        return this._licencesService.buy({ quantity, component_type: componentType });
    }

    public cancel(quantity: number, componentType: LicenceComponentType): Observable<LicenceOverviewResponse> {
        return this._licencesService.cancel({ quantity, component_type: componentType });
    }

    public assign(licenceId: string, userId: string): Observable<LicenceMutationResponse> {
        return this._licencesService.assign(licenceId, { user_id: userId });
    }

    public release(licenceId: string): Observable<LicenceMutationResponse> {
        return this._licencesService.release(licenceId);
    }

    public resume(licenceId: string): Observable<LicenceMutationResponse> {
        return this._licencesService.resume(licenceId);
    }

    // PUBLIC READ MODELS
    readonly overview = computed<LicenceOverview | null>(() => {
        if (!this._overviewResource.hasValue()) {
            return null;
        }
        return this._resourceState.valueOf(this._overviewResource)?.data ?? null;
    });

    readonly components = computed<LicenceComponentSummary[]>(() => this.overview()?.components ?? []);

    readonly availableComponents = computed<LicenceComponentSummary[]>(() =>
        this.components().filter((component) => component.is_available),
    );

    readonly licences = computed<Licence[]>(() => this.overview()?.licences ?? []);

    readonly assignableUsers = computed<User[]>(() => {
        if (!this._usersResource.hasValue()) {
            return [];
        }
        return (this._resourceState.valueOf(this._usersResource)?.data.data ?? [])
            .map((user) => new User(user))
            .filter((user) => user.licence_state === 'unlicensed');
    });

    readonly hasSubscription = computed<boolean>(() => !!this.overview()?.subscription_id);

    readonly isLoading = computed(() => this._overviewResource.isLoading());
    readonly isUsersLoading = computed(() => this._usersResource.isLoading());

    readonly errorMessageKey = computed<string | null>(() => {
        const error = this._overviewResource.error() as HttpErrorResponse | undefined;

        if (!error) {
            return null;
        }

        return (error.error as ApiErrorResponse | undefined)?.message ?? 'shared.api.errors.default';
    });
}
